import { useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { PageHeader } from "@/components/PageHeader";
import { useAutoBatch } from "@/features/simulations/hooks";
import { AutoRunProgress } from "@/features/simulations/auto/AutoRunProgress";
import { RecommendationHero } from "@/features/simulations/auto/RecommendationHero";
import { EngineChangedList } from "@/features/simulations/auto/EngineChangedList";
import { CandidateRanking } from "@/features/simulations/auto/CandidateRanking";
import { ColdStartPanel } from "@/features/simulations/auto/ColdStartPanel";
import { CompareCandidates } from "@/features/simulations/auto/CompareCandidates";
import { SimilarMachineModal } from "@/features/simulations/auto/SimilarMachineModal";
import { AssistantReadingSlot } from "@/features/simulations/auto/AssistantReadingSlot";
import { MachineThroughputCard } from "@/features/simulations/auto/MachineThroughputCard";
import { ApplyScenarioDialog } from "@/features/simulations/applied/ApplyScenarioDialog";
import type { ConfidenceDisplay } from "@/features/simulations/ConfidenceChip";
import type { AutoCandidate } from "@opus-dt/shared-types";

const ACTIVE = new Set(["QUEUED", "RUNNING"]);

// Spec 079 — o candidato do lote → o trio que o chip desenha. `displayedConfidenceLevel` cai em
// `confidenceLevel` (a procedência) para candidato anterior a esta feature, que não tem régua a
// declarar; sem o fallback o histórico de lotes exibiria "—" da noite para o dia.
function toConfidenceDisplay(c: AutoCandidate | null | undefined): ConfidenceDisplay {
  if (!c) return { level: null };
  return {
    level: c.displayedConfidenceLevel ?? c.confidenceLevel,
    basis: c.confidenceBasis,
    trackRecordSource: c.trackRecordSource,
  };
}

// Orquestrador da recomendação automática.
export function SimulationAutoBatchPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isActive = (s?: string) => !!s && ACTIVE.has(s);
  const autoBatchQuery = useAutoBatch(id, 5_000);
  const { data: batch } = autoBatchQuery;
  const [similarFor, setSimilarFor] = useState<{ machineId: string; machineName: string } | null>(
    null
  );
  // Promover a recomendação a COMPROMISSO. Só faz sentido com o lote concluído:
  // comprometer a fábrica com um resultado parcial seria decidir sobre número que ainda vai mudar.
  const [applying, setApplying] = useState(false);

  const onTerminal = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["auto-batches", id] });
  }, [qc, id]);

  // Severidade alta: sem isso a tela ficava em "Carregando…" para sempre quando a
  // query falhava (nunca chegava a `isError`, `batch` simplesmente nunca populava).
  if (autoBatchQuery.isError) {
    return (
      <Notify.QueryError
        error={autoBatchQuery.error}
        onRetry={() => void autoBatchQuery.refetch()}
        title="Não foi possível carregar a recomendação"
      />
    );
  }
  if (!batch) return <p className="text-sm text-muted-foreground">Carregando…</p>;

  const baseline = batch.candidates.find((c) => c.isBaseline) ?? null;
  const winner = batch.candidates.find((c) => c.isWinner) ?? null;

  return (
    <section>
      <PageHeader
        title={`Recomendação · ${batch.lineName}`}
        subtitle="Automático — o motor descobriu a melhor configuração."
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate(`/lines/${batch.lineId}/applied-scenarios`)}
            >
              <ClipboardCheck className="h-4 w-4" />
              Compromissos da linha
            </Button>
            <Button variant="outline" onClick={() => navigate("/simulations")}>
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>
          </>
        }
      />

      {isActive(batch.status) ? (
        <AutoRunProgress
          batchId={batch.id}
          candidateTotal={batch.candidateCount}
          onTerminal={onTerminal}
        />
      ) : (
        <div className="space-y-4">
          {/* O espaço da narrativa vem ANTES dos números: quem abre a tela quer saber o
              que aconteceu, e só depois conferir. O fluxo que preenche ainda não existe. */}
          <AssistantReadingSlot />
          <RecommendationHero batch={batch} winner={winner} baseline={baseline} />
          <div className="flex justify-end">
            <Button disabled={!winner && !baseline} onClick={() => setApplying(true)}>
              Aplicar cenário
            </Button>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-4">
              <EngineChangedList winner={winner} />
              <CandidateRanking
                batch={batch}
                baseline={baseline}
                onOpen={(simId) => navigate(`/simulations/${simId}`)}
              />
            </div>
            <div className="space-y-4">
              <ColdStartPanel
                batch={batch}
                onIndicate={(machineId, machineName) => setSimilarFor({ machineId, machineName })}
              />
              <CompareCandidates batch={batch} />
              <MachineThroughputCard winner={winner} baseline={baseline} />
            </div>
          </div>
        </div>
      )}

      {applying ? (
        <ApplyScenarioDialog
          open
          onOpenChange={setApplying}
          lineId={batch.lineId}
          // Em lote NO_GAIN não há vencedor: o que se compromete é a própria base ("seguir como
          // está"), e o servidor recusa qualquer outro candidato desse lote.
          simulationId={(winner ?? baseline)?.simulationId ?? ""}
          simulatedHours={batch.horizonHours}
          winnerConfidence={toConfidenceDisplay(winner ?? baseline)}
          baselineConfidence={toConfidenceDisplay(baseline)}
          onApplied={() => navigate(`/lines/${batch.lineId}/applied-scenarios`)}
        />
      ) : null}

      {similarFor ? (
        <SimilarMachineModal
          open
          onOpenChange={(o) => !o && setSimilarFor(null)}
          lineId={batch.lineId}
          machineId={similarFor.machineId}
          machineName={similarFor.machineName}
        />
      ) : null}
    </section>
  );
}
